import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';

const API_BASE_URL = '/api';

// SECURITY: Access token stored in memory only (not localStorage)
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/**
 * SECURITY: Read CSRF token from cookie for double-submit pattern
 */
function getCsrfToken(): string | null {
  const match = document.cookie.match(/csrf-token=([^;]+)/);
  return match ? match[1] : null;
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Required for HttpOnly cookies
});

// Request interceptor to add auth token and CSRF token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // SECURITY: Add CSRF token for state-changing requests
    const csrfToken = getCsrfToken();
    if (
      csrfToken &&
      config.method &&
      !['get', 'head', 'options'].includes(config.method.toLowerCase())
    ) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Single-flight refresh: prevent multiple concurrent refresh requests
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Skip refresh logic for login/refresh/logout to avoid infinite loops
    const isAuthEndpoint =
      originalRequest.url === '/auth/login' ||
      originalRequest.url === '/auth/refresh' ||
      originalRequest.url === '/auth/logout';

    // If 401, not already retrying, and not an auth endpoint
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      try {
        // Single-flight: reuse existing refresh promise if one is in progress
        if (!isRefreshing) {
          isRefreshing = true;
          refreshPromise = axios
            .post<{ accessToken: string }>(
              `${API_BASE_URL}/auth/refresh`,
              {},
              { withCredentials: true }
            )
            .then((response) => {
              const newToken = response.data.accessToken;
              setAccessToken(newToken);
              return newToken;
            })
            .catch(() => {
              setAccessToken(null);
              return null;
            })
            .finally(() => {
              isRefreshing = false;
              refreshPromise = null;
            });
        }

        const newAccessToken = await refreshPromise;

        if (newAccessToken) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        } else {
          // Only redirect if not already on login page
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
      } catch {
        // Refresh failed, clear token and redirect
        setAccessToken(null);
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  }
);

export default api;
